import { ApolloQueryResult, useQuery } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { ErrorComponents, InfoPanel, useSidebar, useUpdateTransactionAccount } from '@css-xp/base';
import BillingAdmins from '@css-xp/base/billing-admin/src/billing-admins';
import { withApollo } from '@css-xp/base/graphql-provider';
import { BillingAdmin, CommerceAddress, generatedTypesNew, Maybe } from '@css-xp/graphtypes';
import { TransactionAccountProduct } from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { UpdateTransactionAccountPayload } from '@atlassian/cssxp-types-billing';
import React, { useCallback } from 'react';
import { useParams } from 'react-router';
import { fetchTransactionAccount } from '../queries/fetch-transaction-account-page';
import './deactivate-billing-account-sidebar.scss';
import { AnalyticsTrackAction, AnalyticsTrackSubject, analytics } from '@atlassian/temp-analytics';

interface Props {
  transactionAccountId: string;
  invoiceGroupId: string;
  billingAdmins: BillingAdmin[];
  transactionAccountName?: Maybe<string>;
  transactionAccountNumber?: Maybe<string>;
  billToAddress: Maybe<CommerceAddress>;
  onComplete?: () => Promise<ApolloQueryResult<unknown>>;
}

export const DeactivateBillingAccountSidebar = ({
  transactionAccountId,
  invoiceGroupId,
  billingAdmins,
  transactionAccountName,
  transactionAccountNumber,
  billToAddress,
  onComplete,
}: Props) => {
  const { closeSidebar } = useSidebar();

  const { data, loading, error } = useQuery<
    generatedTypesNew.FetchTransactionAccountV2Query,
    generatedTypesNew.FetchTransactionAccountV2QueryVariables
  >(fetchTransactionAccount, {
    variables: {
      id: transactionAccountId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const productSubscriptionsList = data?.transactionAccount?.productSubscriptions?.nodes;

  const { updateTransactionAccount, isProcessing } = useUpdateTransactionAccount({
    onClose: () => {
      // do nothing
    },
    onComplete: () => {
      onComplete && onComplete();
      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.DEACTIVATE,
        actionSubject: AnalyticsTrackSubject.BILLING_ACCOUNT,
        attributes: {
          transactionAccountName: transactionAccountName,
          transactionAccountNumber: transactionAccountNumber,
        },
      });
    },
    sendNotification: true,
    notification: {
      failureHeading: 'Unable to deactivate account',
      successMessage: `You've deactivated the billing account, ${
        transactionAccountName ?? 'Billing account'
      } ${transactionAccountNumber}`,
    },
  });
  const onSubmit = useCallback(async () => {
    const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
      invoiceGroupId,
      modifiedBillingAdmins: {
        emailsOrAaidsToDelete: billingAdmins.map((billingAdmin) => billingAdmin.email as string),
      },
    };

    await updateTransactionAccount({
      transactionAccountId,
      updateTransactionAccountPayload,
    });
  }, [billToAddress, invoiceGroupId, transactionAccountId]);

  const isActiveSubscription = (productSubscriptionsList ?? []).filter((subscription) => {
    return subscription?.billingStatus === 'ACTIVE';
  });

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  return (
    <div className="deactivate-billing-account">
      <Form onSubmit={onSubmit}>
        {({ formProps, submitting }) => (
          <form {...formProps}>
            <FormHeader
              title="Deactivate billing account"
              description={
                <span>
                  {transactionAccountName ?? 'Billing account' + ' '} {transactionAccountNumber ?? ''}
                </span>
              }
            />
            <FormSection>
              <div className="deactivate-error-info">
                {isActiveSubscription.length > 0 && (
                  <InfoPanel
                    type="error"
                    title="Account contains active subscriptions"
                    message={<p>You can't deactivate a billing account with active subscriptions.</p>}
                  />
                )}
              </div>
              <InfoPanel
                type="warning"
                title="Before you deactivate this account, note that:"
                message={
                  <ul>
                    <li>you’ll need to ensure that this account doesn't have any unpaid invoices</li>
                    <li> all the billing admins will be removed from this account</li>
                  </ul>
                }
              />
            </FormSection>
            <FormSection>
              <BillingAdmins
                billingAdmins={billingAdmins.map((billingAdmin) => ({
                  fullName: billingAdmin.fullName,
                  email: billingAdmin.email,
                }))}
                total={billingAdmins.length}
                loading={false}
                isRemoveDisabled={true}
              />
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="default" onClick={closeSidebar} testId="cancel-button">
                  Cancel
                </Button>
                <LoadingButton
                  appearance="danger"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  isDisabled={isActiveSubscription.length > 0}
                  testId={submitting || isProcessing ? 'loading-button' : 'deactivate-button'}
                >
                  Deactivate
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default withApollo(DeactivateBillingAccountSidebar);
